import { useState, useEffect, useCallback, useMemo } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import AdminLogins from "@/components/AdminLogins";
import AdminRegWatch from "@/components/AdminRegWatch";
import AdminResourceFiles from "@/components/AdminResourceFiles";
import AdminPolicyContent from "@/components/AdminPolicyContent";
import AdminPackLaunch from "@/components/AdminPackLaunch";
import AdminSponsors from "@/components/AdminSponsors";
import AdminTestimonials from "@/components/AdminTestimonials";
import AdminArticles from "@/components/AdminArticles";
import AdminGrowth from "@/components/AdminGrowth";
import AdminActivity from "@/components/AdminActivity";
import AdminSubscribersGlance from "@/components/AdminSubscribersGlance";
import AdminStripeWebhooks from "@/components/AdminStripeWebhooks";
import AdminInvoices from "@/components/AdminInvoices";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Users, PoundSterling, LifeBuoy, Loader2, Send, RefreshCw,
  CheckCircle2, Clock, Mail, MailCheck, MailX, Search, Archive, FolderOpen, Phone, UserPlus, Radar,
} from "lucide-react";
import { toast } from "sonner";
import { logError } from "@/lib/log";

const FILTERS = [
  { id: "open", label: "Open" }, { id: "answered", label: "Answered" },
  { id: "closed", label: "Closed" }, { id: "all", label: "All" },
];


const ESCALATION_BADGE = {
  answered: { cls: "bg-accent/10 text-accent border-0", Icon: CheckCircle2, label: "Answered" },
  closed: { cls: "opacity-70", Icon: Archive, label: "Closed" },
};

function EscalationStatusBadge({ status }) {
  const { cls, Icon, label } = ESCALATION_BADGE[status] || { cls: "", Icon: Clock, label: "Open" };
  return <Badge variant="secondary" className={cls}><Icon className="w-3 h-3 mr-1" /> {label}</Badge>;
}

function UserPlanBadge({ user }) {
  if (user.subscription_status === "active") return <Badge variant="secondary" className="bg-accent/10 text-accent border-0 capitalize">{user.subscription_tier}</Badge>;
  if (user.subscription_status === "expired") return <Badge variant="outline">Expired</Badge>;
  return <span className="text-muted-foreground">Free</span>;
}

export default function Admin() {
  const [stats, setStats] = useState(null);
  const [tab, setTab] = useState("escalations");
  const [escalations, setEscalations] = useState([]);
  const [users, setUsers] = useState([]);

  const setRole = async (u) => {
    const role = u.role === "admin" ? "user" : "admin";
    if (!window.confirm(role === "admin" ? `Give ${u.email} full admin rights?` : `Remove admin rights from ${u.email}?`)) return;
    try {
      await api.patch(`/admin/users/${u.id}/role`, { role });
      setUsers((prev) => prev.map((x) => (x.id === u.id ? { ...x, role } : x)));
      toast.success(role === "admin" ? `${u.email} is now an admin` : `${u.email} is no longer an admin`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Could not update role"); }
  };  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [filter, setFilter] = useState("open");
  const [callbacks, setCallbacks] = useState([]);
  const [search, setSearch] = useState("");

  const load = useCallback(() => {
    Promise.all([
      api.get("/admin/stats").then((r) => setStats(r.data)),
      api.get("/admin/escalations").then((r) => setEscalations(r.data)),
      api.get("/admin/users").then((r) => setUsers(r.data)),
    ]).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const sendReply = async () => {
    if (!reply.trim()) return;
    setSending(true);
    try {
      const { data } = await api.post(`/admin/escalations/${active.id}/respond`, { response: reply });
      toast.success(data.email_sent ? "Reply sent and emailed to the user" : "Reply saved — email could not be delivered");
      setActive(null); setReply("");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const loadCallbacks = useCallback(() => api.get("/admin/callbacks").then((r) => setCallbacks(r.data)).catch((err) => logError("Failed to load callbacks", err)), []);
  useEffect(() => { loadCallbacks(); }, [loadCallbacks]);
  const openCallbacks = useMemo(() => callbacks.filter((c) => c.status === "requested"), [callbacks]);

  const closeQuery = async (e) => {
    try {
      await api.post(`/admin/escalations/${e.id}/close`);
      toast.success("Query closed");
      load();
    } catch { toast.error("Could not close query"); }
  };

  const visible = escalations.filter((e) => {
    if (filter !== "all" && e.status !== filter) return false;
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return [e.question, e.user_email, e.user_name, e.org_name, e.contact_email].some((v) => (v || "").toLowerCase().includes(q));
  });
  const counts = escalations.reduce((acc, e) => ({ ...acc, [e.status]: (acc[e.status] || 0) + 1 }), {});

  const refreshUpdates = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.post("/admin/regulatory-updates/refresh");
      toast.success(`Synced ${data.refreshed} regulatory updates`);
    } catch {
      toast.error("Refresh failed");
    } finally {
      setRefreshing(false);
    }
  };

  const STAT_CARDS = stats ? [
    { label: "Total users", value: stats.total_users, icon: Users },
    { label: "Active subscribers", value: stats.active_subscribers, icon: CheckCircle2 },
    { label: "Revenue", value: `£${stats.total_revenue}`, icon: PoundSterling },
    { label: "Open queries", value: stats.open_escalations, icon: LifeBuoy },
  ] : [];

  if (loading) return <div className="flex justify-center py-32"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="flex items-start justify-between gap-4 mb-8 flex-wrap">
        <div>
          <span className="overline text-accent">Admin</span>
          <h1 className="font-display text-3xl font-bold tracking-tight mt-1">Control centre</h1>
        </div>
        <Button variant="outline" onClick={refreshUpdates} disabled={refreshing} data-testid="admin-refresh-updates-button">
          {refreshing ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <RefreshCw className="w-4 h-4 mr-1.5" />}
          Sync regulatory updates
        </Button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {STAT_CARDS.map((s) => (
          <div key={s.label} className="bg-card border border-border rounded-2xl p-6 shadow-sm" data-testid={`admin-stat-${s.label.replace(/\s/g, "-").toLowerCase()}`}>
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm text-muted-foreground">{s.label}</span>
              <s.icon className="w-5 h-5 text-primary" />
            </div>
            <p className="font-display text-3xl font-extrabold">{s.value}</p>
          </div>
        ))}
      </div>

      <AdminSubscribersGlance stats={stats} onViewAll={() => setTab("subscribers")} />
      <AdminStripeWebhooks />

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-6">
          <TabsTrigger value="escalations" data-testid="admin-tab-escalations">
            Inbox {stats?.open_escalations ? `(${stats.open_escalations})` : ""}
          </TabsTrigger>
          <TabsTrigger value="logins" data-testid="admin-tab-logins"><UserPlus className="w-3.5 h-3.5 mr-1.5" /> Logins &amp; invites</TabsTrigger>
          <TabsTrigger value="resources" data-testid="admin-tab-resources">Catalogue &amp; documents</TabsTrigger>
          <TabsTrigger value="regwatch" data-testid="admin-tab-regwatch"><Radar className="w-3.5 h-3.5 mr-1.5" /> Regulatory watch</TabsTrigger>
          <TabsTrigger value="sponsors" data-testid="admin-tab-sponsors">Advertisers</TabsTrigger>
          <TabsTrigger value="testimonials" data-testid="admin-tab-testimonials">Testimonials</TabsTrigger>
          <TabsTrigger value="articles" data-testid="admin-tab-articles">Articles</TabsTrigger>
          <TabsTrigger value="growth" data-testid="admin-tab-growth">Growth</TabsTrigger>
          <TabsTrigger value="activity" data-testid="admin-tab-activity">Activity</TabsTrigger>
          <TabsTrigger value="subscribers" data-testid="admin-tab-subscribers">Subscribers</TabsTrigger>
          <TabsTrigger value="invoices" data-testid="admin-tab-invoices">Invoices</TabsTrigger>
        </TabsList>

        <TabsContent value="invoices"><AdminInvoices /></TabsContent>
        <TabsContent value="logins"><AdminLogins /></TabsContent>
        <TabsContent value="regwatch"><AdminRegWatch /></TabsContent>

        <TabsContent value="escalations">
          {openCallbacks.length > 0 && (
            <div className="mb-6 bg-card border border-accent/30 rounded-2xl p-5" data-testid="admin-callbacks">
              <p className="font-display font-semibold mb-3 flex items-center gap-2"><Phone className="w-4 h-4 text-accent" /> Call requests</p>
              <ul className="space-y-2">
                {openCallbacks.map((c) => (
                  <li key={c.id} className="flex items-start justify-between gap-3 text-sm bg-secondary/40 rounded-xl p-3" data-testid={`admin-callback-${c.id}`}>
                    <div className="min-w-0">
                      <p className="font-medium">{c.user_name || c.user_email}{c.org_name ? ` · ${c.org_name}` : ""} — <a href={`tel:${c.phone}`} className="text-primary">{c.phone}</a></p>
                      <p className="text-xs text-muted-foreground">{new Date(c.preferred_date).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" })} · {c.slot}</p>
                      <p className="text-xs mt-1 whitespace-pre-wrap">{c.topic}</p>
                    </div>
                    <Button size="sm" variant="outline" onClick={async () => { await api.post(`/admin/callbacks/${c.id}/done`); loadCallbacks(); }} data-testid={`admin-callback-done-${c.id}`}>Done</Button>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div className="flex items-center gap-3 mb-4 flex-wrap">
            <div className="flex gap-1 bg-secondary/60 rounded-lg p-1">
              {FILTERS.map((f) => (
                <button key={f.id} onClick={() => setFilter(f.id)} data-testid={`inbox-filter-${f.id}`}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${filter === f.id ? "bg-card shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"}`}>
                  {f.label}{f.id !== "all" && counts[f.id] ? <span className="ml-1.5 text-xs opacity-70">{counts[f.id]}</span> : null}
                </button>
              ))}
            </div>
            <div className="relative flex-1 min-w-[220px] max-w-sm">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search questions, names, emails…" className="pl-9" data-testid="inbox-search-input" />
            </div>
          </div>
          <div className="space-y-3" data-testid="inbox-list">
            {visible.length ? visible.map((e) => (
              <div key={e.id} className="bg-card border border-border rounded-2xl p-6 shadow-sm" data-testid={`admin-escalation-${e.id}`}>
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <EscalationStatusBadge status={e.status} />
                      <span className="text-sm text-muted-foreground flex items-center gap-1">
                        <Mail className="w-3.5 h-3.5" /> {e.contact_email}
                      </span>
                      {e.admin_response && (
                        <span className={`text-xs flex items-center gap-1 ${e.email_sent ? "text-accent" : "text-amber-600"}`} data-testid={`email-status-${e.id}`}>
                          {e.email_sent ? <><MailCheck className="w-3.5 h-3.5" /> Emailed</> : <><MailX className="w-3.5 h-3.5" /> Email failed</>}
                        </span>
                      )}
                      <span className="text-xs text-muted-foreground ml-auto">{new Date(e.created_at).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
                    </div>
                    <p className="font-medium whitespace-pre-wrap">{e.question}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      From {e.user_name || e.user_email}{e.org_name ? ` · ${e.org_name}` : ""}
                    </p>
                    {e.admin_response && (
                      <div className="mt-3 text-sm bg-accent/5 border border-accent/20 rounded-lg p-3">
                        <p className="text-xs font-semibold text-accent mb-1">Your reply</p>
                        <p className="text-muted-foreground whitespace-pre-wrap">{e.admin_response}</p>
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2 shrink-0">
                    {e.status !== "closed" && (
                      <Button size="sm" variant={e.status === "answered" ? "outline" : "default"}
                        onClick={() => { setActive(e); setReply(e.admin_response || ""); }}
                        data-testid={`admin-reply-button-${e.id}`}>
                        <Send className="w-4 h-4 mr-1.5" /> {e.status === "answered" ? "Edit reply" : "Reply"}
                      </Button>
                    )}
                    {e.status === "answered" && (
                      <Button size="sm" variant="ghost" onClick={() => closeQuery(e)} data-testid={`admin-close-button-${e.id}`}>
                        <Archive className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            )) : (
              <div className="text-center text-muted-foreground py-16 flex flex-col items-center gap-2">
                <FolderOpen className="w-8 h-8 opacity-50" />
                <p>{filter === "open" ? "Inbox zero — no open queries." : "Nothing here."}</p>
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="resources">
          <AdminPolicyContent />
          <div className="mt-6"><AdminPackLaunch /></div>
          <div className="mt-6"><AdminResourceFiles /></div>
        </TabsContent>

        <TabsContent value="sponsors">
          <AdminSponsors />
        </TabsContent>

        <TabsContent value="testimonials">
          <AdminTestimonials />
        </TabsContent>

        <TabsContent value="articles">
          <AdminArticles />
        </TabsContent>

        <TabsContent value="growth">
          <AdminGrowth />
        </TabsContent>
        <TabsContent value="activity"><AdminActivity /></TabsContent>

        <TabsContent value="subscribers">
          <div className="bg-card border border-border rounded-2xl shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-secondary/50 text-left">
                  <tr>
                    <th className="px-6 py-3 font-semibold">Name</th>
                    <th className="px-6 py-3 font-semibold">Email</th>
                    <th className="px-6 py-3 font-semibold">Organisation</th>
                    <th className="px-6 py-3 font-semibold">Plan</th>
                    <th className="px-6 py-3 font-semibold">Renews</th>
                    <th className="px-6 py-3 font-semibold">Purchases</th>
                    <th className="px-6 py-3 font-semibold">Terms accepted</th>
                    <th className="px-6 py-3 font-semibold">Role</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id} className="border-t border-border" data-testid={`admin-user-row-${u.id}`}>
                      <td className="px-6 py-3">{u.name || "—"}</td>
                      <td className="px-6 py-3 text-muted-foreground">{u.email}</td>
                      <td className="px-6 py-3 text-muted-foreground">{u.org_name || "—"}</td>
                      <td className="px-6 py-3">
                        <UserPlanBadge user={u} />
                      </td>
                      <td className="px-6 py-3 text-muted-foreground text-xs">
                        {u.subscription_status === "active" && u.subscription_period_end
                          ? new Date(u.subscription_period_end).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "—"}
                      </td>
                      <td className="px-6 py-3">{u.purchased_lookup_keys?.length || 0}</td>
                      <td className="px-6 py-3 text-muted-foreground text-xs whitespace-nowrap" data-testid={`admin-user-terms-${u.id}`}>
                        {u.terms_version ? <>v{u.terms_version}<br />{new Date(u.terms_accepted_at).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}</>
                          : u.privacy_consent_at ? <>pre-versioning<br />{new Date(u.privacy_consent_at).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}</> : "—"}
                      </td>
                      <td className="px-6 py-3 whitespace-nowrap">
                        <Button size="sm" variant={u.role === "admin" ? "secondary" : "ghost"} className="text-xs" onClick={() => setRole(u)} data-testid={`admin-user-role-${u.id}`}>
                          {u.role === "admin" ? "Admin · remove" : "Make admin"}
                        </Button>
                      </td>
                    </tr>
                  ))}
                  {!users.length && (
                    <tr><td colSpan={8} className="px-6 py-12 text-center text-muted-foreground">No subscribers yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent data-testid="admin-reply-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Reply to specialist query</DialogTitle>
          </DialogHeader>
          {active && (
            <div className="space-y-4">
              <div className="bg-secondary/50 rounded-lg p-3 text-sm">
                <p className="text-xs text-muted-foreground mb-1">Question from {active.contact_email}</p>
                <p>{active.question}</p>
              </div>
              <div>
                <Label htmlFor="reply">Your response</Label>
                <Textarea id="reply" value={reply} onChange={(e) => setReply(e.target.value)} rows={5}
                  className="mt-1.5" data-testid="admin-reply-textarea" placeholder="Write your specialist response…" />
                <p className="text-xs text-muted-foreground mt-1.5 flex items-center gap-1"><MailCheck className="w-3.5 h-3.5" /> The user is notified in-app and emailed at {active.contact_email}.</p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setActive(null)}>Cancel</Button>
            <Button onClick={sendReply} disabled={sending} data-testid="admin-send-reply-button">
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-1.5" /> Send reply</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
